import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type DuplicateParams = {
  copyPath: string;
  number?: string;
  title?: string;
  apPayeeName?: string | null;
  totalAmount?: string | null;
};

type ConfirmDuplicateModalContextType = {
  onOpenConfirmDuplicateModal: (params: DuplicateParams) => Promise<void>;
  onCloseConfirmDuplicateModal: () => void;
  duplicateModalOpen: boolean;
  duplicateParams: DuplicateParams | null;
};

const Context = createContext<ConfirmDuplicateModalContextType>({
  onOpenConfirmDuplicateModal: () => Promise.resolve(),
  onCloseConfirmDuplicateModal: () => {},
  duplicateModalOpen: false,
  duplicateParams: null,
});

type ConfirmDuplicateModalProviderProps = PropsWithChildren;

export const ConfirmDuplicateModalProvider: FC<
  ConfirmDuplicateModalProviderProps
> = ({ children }) => {
  const [duplicateParams, setDuplicateParams] =
    useState<DuplicateParams | null>(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const onOpenConfirmDuplicateModal = useCallback(
    (params: DuplicateParams): Promise<void> => {
      setDuplicateParams(params);
      setDuplicateModalOpen(true);
      return Promise.resolve();
    },
    []
  );

  const onCloseConfirmDuplicateModal = useCallback(() => {
    setDuplicateModalOpen(false);
    setDuplicateParams(null);
  }, []);

  const value = useMemo(
    () => ({
      onOpenConfirmDuplicateModal,
      onCloseConfirmDuplicateModal,
      duplicateModalOpen,
      duplicateParams,
    }),
    [
      onOpenConfirmDuplicateModal,
      onCloseConfirmDuplicateModal,
      duplicateModalOpen,
      duplicateParams,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

ConfirmDuplicateModalProvider.displayName = 'ConfirmDuplicateModalProvider';

export const useConfirmDuplicateModal = () => useContext(Context);
